import { pinchRatio } from './touch';

/**
 * The follow camera's orbit around your own avatar: drag to look around, wheel or pinch to
 * zoom, double-click to snap back. Walking eases it back behind you, as SL viewers do, so you
 * see where you're going.
 *
 * Pure state; `attachOrbitControls` wires it to a canvas. The engine gets the result once
 * per frame (`engine.setCameraOrbit`) and clamps it again on its side.
 */

/** Matches the engine's default: 6 m behind, 2.5 m up (CAMERA_DEFAULT_* in scene.rs). */
export const DEFAULT_PITCH = Math.atan2(2.5, 6);
export const DEFAULT_DISTANCE = Math.hypot(6, 2.5);

const PITCH_RANGE: readonly [number, number] = [0, 1.4];
const DISTANCE_RANGE: readonly [number, number] = [2, 30];
/** Radians per dragged pixel. */
const DRAG_SPEED = 0.006;
/** How fast walking swings the camera back behind the avatar (1/s). */
const RECENTER_RATE = 3;

const clamp = (x: number, [lo, hi]: readonly [number, number]): number => Math.min(hi, Math.max(lo, x));

export class OrbitCamera {
  /** Relative to behind the avatar; positive swings round to its right. */
  yaw = 0;
  pitch = DEFAULT_PITCH;
  distance = DEFAULT_DISTANCE;
  /** True while a drag is in progress: walking doesn't fight the user's hand. */
  dragging = false;

  /**
   * A pointer drag by (dx, dy) pixels, as if grabbing the world: it follows the pointer,
   * like most orbit controls (three.js, Blender). Dragging right swings the camera round to
   * the avatar's right; dragging down raises it.
   */
  drag(dx: number, dy: number): void {
    this.yaw = wrap(this.yaw + dx * DRAG_SPEED);
    this.pitch = clamp(this.pitch + dy * DRAG_SPEED, PITCH_RANGE);
  }

  /** A wheel step (`WheelEvent.deltaY`, positive = away): zoom by a constant ratio per step. */
  zoom(deltaY: number): void {
    this.distance = clamp(this.distance * Math.exp(deltaY * 0.001), DISTANCE_RANGE);
  }

  /** A pinch: `ratio` > 1 (fingers spreading) brings the camera closer, by that factor. */
  zoomBy(ratio: number): void {
    if (!(ratio > 0) || !Number.isFinite(ratio)) return;
    this.distance = clamp(this.distance / ratio, DISTANCE_RANGE);
  }

  reset(): void {
    this.yaw = 0;
    this.pitch = DEFAULT_PITCH;
    this.distance = DEFAULT_DISTANCE;
  }

  /** Per frame. While walking (and not dragging), the yaw eases back to behind the avatar. */
  update(dt: number, walking: boolean): void {
    if (walking && !this.dragging) {
      this.yaw *= Math.exp(-RECENTER_RATE * Math.max(0, dt));
    }
  }
}

/** Into (−π, π], so easing back always takes the short way round. */
function wrap(angle: number): number {
  const turn = 2 * Math.PI;
  return angle - turn * Math.round(angle / turn);
}

/**
 * Pointer drag (mouse or one finger), two-finger pinch, wheel and double-click on `canvas`
 * drive `orbit`.
 */
export function attachOrbitControls(canvas: HTMLElement, orbit: OrbitCamera): void {
  // Fingers (or the mouse) currently down on the canvas, by pointer id.
  const down = new Map<number, { x: number; y: number }>();
  // The two fingers' distance at the last move, while pinching.
  let pinch: number | null = null;
  const spread = (): number => {
    const [a, b] = [...down.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  };
  canvas.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || down.size >= 2) return;
    down.set(e.pointerId, { x: e.clientX, y: e.clientY });
    orbit.dragging = true;
    if (down.size === 2) pinch = spread();
    // Keep receiving moves when the pointer leaves the canvas mid-drag. Throws for pointers
    // the browser doesn't consider active (synthetic or some pen input); the drag still
    // works without capture, so that's not worth breaking the handler over.
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {
      /* no capture: fine */
    }
  });
  canvas.addEventListener('pointermove', (e) => {
    const was = down.get(e.pointerId);
    if (!was) return;
    down.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (down.size === 2 && pinch !== null) {
      // Two fingers zoom; they don't also turn the view.
      const now = spread();
      orbit.zoomBy(pinchRatio(pinch, now));
      pinch = now;
    } else {
      orbit.drag(e.clientX - was.x, e.clientY - was.y);
    }
  });
  const end = (e: PointerEvent): void => {
    if (!down.delete(e.pointerId)) return;
    // Lifting one of two fingers carries on as a drag with the other, from where it is.
    pinch = down.size === 2 ? spread() : null;
    if (down.size === 0) orbit.dragging = false;
  };
  canvas.addEventListener('pointerup', end);
  canvas.addEventListener('pointercancel', end);
  // Not passive: the page must not scroll while zooming the 3D view.
  canvas.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      orbit.zoom(e.deltaY);
    },
    { passive: false },
  );
  canvas.addEventListener('dblclick', () => orbit.reset());
}
